<template>
<div>
  <div :style="($ml.current === 'EN') ? 'display:block;' : 'display:none;'" >
    <vue-markdown style="overflow: auto;" @rendered="onrendered">

  There are two possible constructors for the QuanteecConfig object:

  ```kotlin
  val quanteecConfig = QuanteecConfig(
    "videoID", // [Can be an empty String but not recommended] String --> specific name to identify a video. Useful if the videosource url contains a different token for every user. Default: the videoSource value is used.
    "myquanteecKey" // [Mandatory] String --> used to verify the authorizations.
  )

  val quanteecConfig = QuanteecConfig(
    "videoID", // [Can be an empty String but not recommended] String --> specific name to identify a video. Useful if the videosource url contains a different token for every user. Default: the videoSource value is used.
    "myquanteecKey", // [Mandatory] String --> used to verify the authorizations.
    "videoAnalyticsID", // String --> [Can be an empty String] specific name to identify a video for the Analytics process. Default, videoID is used.
    true, // Boolean --> Activate/deactivate Quanteec, including the connection with the controller. This value overrides p2p.activated which does not stop the connection to the controller.
    false, // Boolean --> Send the HTTP requests with credentials.
    false, // Boolean --> Used for detection of live interruption, by activating error detection.
    true, // Boolean --> Activate QUANTEEC P2P system.
    1.0, // Double --> Percentage of upload to use. Number between 0 and 1. If 0, nothing is sent, but the peer still receives data.
    "wss://tracker_url" // [Can be an empty String] --> URL of the tracker. Used only in specific scenarios where the signalling server is deployed on premise.
    
  );
  ```
  [Contact us](mailto:support@quanteec.com) if you need some specific options.
      </vue-markdown>
  </div>
  <div :style="($ml.current === 'FR') ? 'display:block;' : 'display:none;'">
    <vue-markdown style="overflow: auto;" @rendered="onrendered">

 Il existe deux constructeurs différents pour l'objet QuanteecConfig :

 ```kotlin
  val quanteecConfig = QuanteecConfig(
    "videoID", // [Peut être vide mais non recommandé] String --> Nom spécifique pour identifier une vidéo. Important si l'url de la source contient un token qui peut être différent pour chaque utilisateur. Par défaut : l'url de la vidéo est utilisé.
    "myquanteecKey" // [Obligatoire] String --> Utilisé pour vérifier les autorisations.

  val quanteecConfig = QuanteecConfig(
    "videoID", // [Peut être vide mais non recommandé] String --> Nom spécifique pour identifier une vidéo. Important si l'url de la source contient un token qui peut être différent pour chaque utilisateur. Par défaut : l'url de la vidéo est utilisé.
    "myquanteecKey", // [Obligatoire] String -->  Utilisé pour vérifier les autorisations.
    "videoAnalyticsID", // String --> [Peut être vide] Nom spécifique utilisé pour identifier une vidéo dans les Analytics. Par défaut, le videoID est utilisé.
    true, // Boolean --> Active ou désactive Quanteec, y compris la connexion avec le controller. Cette valeur écrase p2p.activated qui ne désactive pas la connexion au controller.
    false, //Boolean --> Inclus les credentials dans les requêtes HTTP.
    false, // Boolean --> Utilisé pour détecter l'interruption des diffusions en direct.
    true, // Boolean --> Active le système pair-à-pair de QUANTEEC.
    1.0, // Double --> Définit le pourcentage de la valeur d'upload mesurée à utiliser. Nombre entre 0 et 1. Si 0, rien n'est envoyé, mais le pair peut continuer à recevoir.
    "wss://tracker_url" // [Peut être vide] --> URL du tracker. Utilisé uniquement dans des scénarios spécifiques où le serveur de signalling est déployé sur site client.
    
  );
  ```
[Contactez nous](mailto:support@quanteec.com) si vous avez besoin d'options spécifiques.
    </vue-markdown>
  </div>

<h4>API Java/Kotlin</h4>

<div :style="($ml.current === 'EN') ? 'display:block;' : 'display:none;'" >
    <vue-markdown style="overflow: auto;" @rendered="onrendered">
  ``` kotlin
  quanteecCore.setPlayer(exoplayer) // Register the exoplayer inside the Quanteec plugin.

  quanteecCore.activateQuanteec(true); // Activate Quanteec and mesh-delivery.
  quanteecCore.activateQuanteec(false); // Deactivate Quanteec and mesh-delivery. All connections to the controller will be stopped.

  quanteecCore.activateP2P(true); // Activate mesh-delivery.
  quanteecCore.activateP2P(false); // Deactivate mesh-delivery.

  quanteecCore.lowLatency = true; //Activate the LowLatency mode for the QUANTEEC plugin (only with quanteecPlugin v1.0 and QuanteecExoPlayer v1.1)
  quanteecCore.lowLatency = false; //Deactivate the LowLatency mode for the QUANTEEC plugin (only with quanteecPlugin v1.0 and QuanteecExoPlayer v1.1)

  greenScore = quanteecCore.GreenScore // Return the green score, the data in p2p divided by the data in http.

  energy = quanteecCore.EnergySavedInWh // Return the total energy saved in Wh.
  energy = quanteecCore.EnergySavedReceivedInWh // Return the energy saved by the data received in Wh.
  energy = quanteecCore.EnergySavedSentInWh // Return the energy saved by the data sent in Wh.
  energy = quanteecCore.getEnergySavedInLight // Return the energy saved in lights switched on during 5 minutes.
  energy = quanteecCore.EnergySavedInCO2g // Return the energy saved in g of CO2.
  energy = quanteecCore.EnergySavedInWatercL // Return the energy saved in cL of water.

  data = quanteecCore.dataCDN // Return the quantity of data in MB downloaded from CDN.
  data = quanteecCore.dataPeer // Return the quantity of data in MB downloaded from other peers.
  data = quanteecCore.dataSent // Return the quantity of data in MB sent to other peers.
  P2PPercent = quanteecCore.p2PPercent // Return the percentage of P2P.

  peerConnected = quanteecCore.peerConnected //Return the number of connected peers to this player.

  quanteecCore.setMaxUploadPercent(value) // Define the maximum percentage of upload bandwidth to use, between 0 and 1. At 0, the peer does not send anything, but can receive data.
  
  
  ```
  [Contact us](mailto:support@quanteec.com) if you need some specific functions.
      </vue-markdown>
  </div>
  <div :style="($ml.current === 'FR') ? 'display:block;' : 'display:none;'">
    <vue-markdown style="overflow: auto;" @rendered="onrendered">

  ```kotlin
  quanteecCore.setPlayer(exoplayer) // Enregistre l'instance exoplayer dans le plugin Quanteec.


  quanteecPlayer.activateQuanteec(true); // Active Quanteec et la distribution en pair-à-pair .
  quanteecPlayer.activateQuanteec(false); // Désactive Quanteec et la distribution en pair-à-pair.  

  quanteecPlayer.activateP2P(true); // Active la diffusion en pair-à-pair.
  quanteecPlayer.activateP2P(false); // Désactive la diffusion en pair-à-pair.

  quanteecCore.lowLatency = true; //Active le mode low latency dy plugin QUANTEEC (Seulement pour les version 1.0 de la librairie quanteecPlugin et 1.1 de la librairie quanteecExoPlugin)
  quanteecCore.lowLatency = false; //Désactive le mode low latency dy plugin QUANTEEC (Seulement pour les version 1.0 de la librairie quanteecPlugin et 1.1 de la librairie quanteecExoPlugin)

  greenScore = quanteecPlayer.GreenScore // Retourne le green score, le rapport des données en p2p sur les données reçues en HTTP.

  energy = quanteecPlayer.EnergySavedInWh // Retourne l'énergie économisée totale en Wh.
  energy = quanteecPlayer.EnergySavedReceivedInWh // Retourne l'énergie économisée par les données reçues en Wh.
  energy = quanteecPlayer.EnergySavedSentInWh // Retourne l'énergie économisée par les données envoyées en Wh.
  energy = quanteecPlayer.EnergySavedInLight // Retourne l'énergie économisée par rapport à une ampoule allumée 5 minutes.
  energy = quanteecPlayer.EnergySavedInCO2g // Retourne l'énergie économisée en g de CO2.
  energy = quanteecPlayer.EnergySavedInWatercL // Retourne l'énergie économisée en cL d'eau.

  data = quanteecCore.dataCDN // Retourne la quantité de données en MB téléchargées du CDN.
  data = quanteecCore.dataPeer // Retourne la quantité de données en MB téléchargées en P2P.
  data = quanteecCore.dataSent // Retourne la quantité de données en MB envoyées aux autres peers.
  P2PPercent = quanteecCore.p2PPercent // Retourne le pourcentage de P2P.

  peerConnected = quanteecCore.peerConnected // Retourne le nombre de peers connectés à ce player.

  quanteecPlayer.setMaxUploadPercent(value) // Définit le pourcentage maximum de la bande-passante montante mesurée à utiliser, entre 0 et 1. À 0, le pair n'envoie rien, mais peut recevoir.
  
  ```
  [Contactez nous](mailto:support@quanteec.com) si vous avez besoin de fonctions spécifiques.
    </vue-markdown>
  </div>


</div>
</template>
<script>
import VueMarkdown from 'vue-markdown';
import Prism from 'prismjs';
import 'prismjs/themes/prism-twilight.css';
export default {
  name: 'ConfigDoc',
  data: function() {
    return {
     
    }
  },
  components: {
      VueMarkdown
  },
  mounted: function() {
 
  },
  methods: {
     onrendered() {
        Prism.highlightAll()
     }
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">

</style>